import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { EditIcon } from './icons/EditIcon';
import { DeleteIcon } from './icons/DeleteIcon';
import { getContrastColor } from '../services/utils';
import { ConfirmModal } from './ConfirmModal';
import { Spinner } from './Spinner';
import { MagicIcon } from './icons/MagicIcon';
// FIX: Replaced missing geminiService with apiService and types
import { apiService } from '../services/apiService';
import { ColorSuggestion, MergeSuggestionGroup } from '../types';
import { ColorSuggestionModal } from './ColorSuggestionModal';
import { useSettings } from '../hooks/useSettings';
import { ManageableType } from '../contexts/SettingsContext';
import { Currency, SettingItem } from '../types';
import { GitMergeIcon } from './icons/GitMergeIcon';
import { MergeSuggestionModal } from './MergeSuggestionModal';
import { Pagination } from './Pagination';


// Tento typ je dostatečně obecný pro všechny spravované položky.
export type DraggableItem = {
    id: number | string;
    name: string;
    color?: string;
    count?: number;
};

interface SettingsManagerProps {
    title: string;
    items: DraggableItem[];
    onAddItem?: (item: { name: string; color?: string }) => Promise<void>;
    onUpdateItem: (item: DraggableItem) => Promise<void>;
    onDeleteItem?: (id: number | string) => void;
    onMergeItems: (sourceId: number | string, targetId: number | string) => Promise<void>;
    hasColor?: boolean;
    isAddable?: boolean;
    isDeletable?: boolean;
    showFilter?: boolean;
    isRefreshing?: boolean;
    activeViewId?: ManageableType | 'businesses';
    pageSize: number;
    onPageSizeChange: (size: number) => void;
}

const DROP_TARGET_CLASSES = ['bg-blue-100', 'ring-2', 'ring-blue-400', 'ring-inset'];
const DRAGGED_ITEM_CLASS = 'opacity-30';

export const SettingsManager: React.FC<SettingsManagerProps> = ({ 
    title, 
    items, 
    onAddItem, 
    onUpdateItem, 
    onDeleteItem, 
    onMergeItems, 
    hasColor = true,
    isAddable = true,
    isDeletable = true,
    showFilter = false,
    isRefreshing = false,
    activeViewId,
    pageSize,
    onPageSizeChange,
}) => {
    const { saveAllItems, colorHistory, addColorToHistory, perItemColorHistory, addColorToItemHistory } = useSettings();
    const [name, setName] = useState('');
    const [color, setColor] = useState('#3B82F6');
    const [editingItem, setEditingItem] = useState<DraggableItem | null>(null);
    const [itemToDelete, setItemToDelete] = useState<DraggableItem | null>(null);
    const [filter, setFilter] = useState('');
    const [formError, setFormError] = useState<string | null>(null);

    // --- Drag and Drop State ---
    const [mergeConfirmation, setMergeConfirmation] = useState<{ source: DraggableItem; target: DraggableItem } | null>(null);
    const ghostRef = useRef<HTMLDivElement>(null);
    const listRef = useRef<HTMLUListElement>(null);
    const draggedItemRef = useRef<DraggableItem | null>(null);
    const draggedElementRef = useRef<HTMLLIElement | null>(null);
    const dropTargetIdRef = useRef<number | string | null>(null);
    const scrollIntervalRef = useRef<number | null>(null);

    // --- AI Suggestions State ---
    const [isSuggesting, setIsSuggesting] = useState(false);
    const [suggestions, setSuggestions] = useState<ColorSuggestion[] | null>(null);
    const [suggestionError, setSuggestionError] = useState<string | null>(null);

    const [isSuggestingMerge, setIsSuggestingMerge] = useState(false);
    const [mergeSuggestions, setMergeSuggestions] = useState<{ target: DraggableItem, sources: DraggableItem[] }[] | null>(null);
    const [mergeSuggestionError, setMergeSuggestionError] = useState<string | null>(null);
    
    // --- Pagination State ---
    const [currentPage, setCurrentPage] = useState(1);


    const filteredItems = useMemo(() => 
        items.filter(item => item.name.toLowerCase().includes(filter.toLowerCase()))
    , [items, filter]);

    useEffect(() => {
        setCurrentPage(1);
    }, [filter, items]);

    const paginatedItems = useMemo(() => {
        if (pageSize === 0) { // 0 represents "All"
            return filteredItems;
        }
        const startIndex = (currentPage - 1) * pageSize;
        return filteredItems.slice(startIndex, startIndex + pageSize);
    }, [filteredItems, currentPage, pageSize]);

    const totalPages = useMemo(() => {
        if (pageSize === 0 || filteredItems.length === 0) {
            return 1;
        }
        return Math.ceil(filteredItems.length / pageSize);
    }, [filteredItems.length, pageSize]);

    const handlePageSizeChangeInternal = (e: React.ChangeEvent<HTMLSelectElement>) => {
        onPageSizeChange(Number(e.target.value));
        setCurrentPage(1);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setFormError(null);
        if (!name.trim()) return;

        if (editingItem) {
            try {
                const updatedItem = { ...editingItem, name: name.trim() };
                if (hasColor && 'color' in updatedItem) {
                    updatedItem.color = color;
                }
                
                await onUpdateItem(updatedItem);

                // --- HISTORY LOGIC --- (run after successful update)
                if (hasColor && editingItem.color && color.toLowerCase() !== editingItem.color.toLowerCase()) {
                    // Add the NEW color to the global history
                    await addColorToHistory(color);
                    
                    // Add the OLD color to the item-specific history
                    if (activeViewId && activeViewId !== 'businesses' && typeof editingItem.id === 'number') {
                        await addColorToItemHistory(activeViewId as ManageableType, editingItem.id, editingItem.color);
                    }
                }

                handleCancelEdit();
            } catch (error: any) {
                setFormError(error.message || 'Položku se nepodařilo aktualizovat.');
            }
        } else if (onAddItem) {
            try {
                await onAddItem({ name: name.trim(), color: hasColor ? color : undefined });
                if (hasColor) addColorToHistory(color);
                setName('');
                setColor('#3B82F6');
            } catch (error: any) {
                setFormError(error.message || 'Položku se nepodařilo přidat.');
            }
        }
    };
    
    const handleEditClick = (item: DraggableItem) => {
        setEditingItem(item);
        setName(item.name);
        if (hasColor && item.color) {
            setColor(item.color);
        }
        setFormError(null);
    };

    const handleCancelEdit = () => {
        setEditingItem(null);
        setName('');
        setColor('#3B82F6');
        setFormError(null);
    };

    const handleConfirmDelete = () => {
        if(itemToDelete && itemToDelete.id && onDeleteItem) {
            onDeleteItem(itemToDelete.id);
            setItemToDelete(null);
        }
    };

    // --- Drag and Drop Logic ---

    const cleanupDragState = useCallback(() => {
        if (draggedElementRef.current) {
            draggedElementRef.current.classList.remove(DRAGGED_ITEM_CLASS);
            draggedElementRef.current = null;
        }
        if (dropTargetIdRef.current && listRef.current) {
            const lastTarget = listRef.current.querySelector(`[data-item-id="${dropTargetIdRef.current}"]`);
            lastTarget?.classList.remove(...DROP_TARGET_CLASSES);
        }
        if (ghostRef.current) {
          ghostRef.current.style.display = 'none';
        }
        draggedItemRef.current = null;
        dropTargetIdRef.current = null;
      }, []);

    // --- Mouse Handlers ---
    const handleDragStart = (e: React.DragEvent<HTMLLIElement>, item: DraggableItem) => {
        draggedItemRef.current = item;
        draggedElementRef.current = e.currentTarget;
        e.currentTarget.classList.add(DRAGGED_ITEM_CLASS);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', item.id.toString());
    };
    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
    }
    const handleDragEnter = (e: React.DragEvent<HTMLLIElement>, targetItem: DraggableItem) => {
        e.preventDefault();
        if (draggedItemRef.current && draggedItemRef.current.id !== targetItem.id) {
            if (dropTargetIdRef.current && listRef.current) {
                const oldTarget = listRef.current.querySelector(`[data-item-id="${dropTargetIdRef.current}"]`);
                oldTarget?.classList.remove(...DROP_TARGET_CLASSES);
            }
            dropTargetIdRef.current = targetItem.id;
            e.currentTarget.classList.add(...DROP_TARGET_CLASSES);
        }
    };
    const handleDrop = (e: React.DragEvent<HTMLLIElement>, targetItem: DraggableItem) => {
        e.preventDefault();
        if (draggedItemRef.current && draggedItemRef.current.id !== targetItem.id) {
            setMergeConfirmation({ source: draggedItemRef.current, target: targetItem });
        }
        cleanupDragState();
    };
    const handleDragEnd = () => {
        cleanupDragState();
    };

    // --- Touch Handlers ---
    const stopScrolling = useCallback(() => {
        if (scrollIntervalRef.current) {
            cancelAnimationFrame(scrollIntervalRef.current);
            scrollIntervalRef.current = null;
        }
    }, []);

    const handleTouchMove = useCallback((e: globalThis.TouchEvent) => {
        if (!draggedItemRef.current) return;
        
        e.preventDefault();
    
        const touch = e.touches[0];
        const ghost = ghostRef.current;
        if (ghost) {
          ghost.style.left = `${touch.clientX}px`;
          ghost.style.top = `${touch.clientY}px`;
        }
    
        const scrollZone = 60;
        const scrollSpeed = 10;
        if (touch.clientY < scrollZone) {
          stopScrolling();
          scrollIntervalRef.current = requestAnimationFrame(function scrollUp() {
            window.scrollBy(0, -scrollSpeed);
            scrollIntervalRef.current = requestAnimationFrame(scrollUp);
          });
        } else if (touch.clientY > window.innerHeight - scrollZone) {
          stopScrolling();
          scrollIntervalRef.current = requestAnimationFrame(function scrollDown() {
            window.scrollBy(0, scrollSpeed);
            scrollIntervalRef.current = requestAnimationFrame(scrollDown);
          });
        } else {
          stopScrolling();
        }
        
        if (ghost) ghost.style.visibility = 'hidden';
        const elementBelow = document.elementFromPoint(touch.clientX, touch.clientY);
        if (ghost) ghost.style.visibility = 'visible';
    
        const targetLi = elementBelow?.closest('li[data-item-id]');
        let newDropTargetId: number | string | null = null;
        if (targetLi) {
            const targetIdStr = (targetLi as HTMLLIElement).dataset.itemId;
            if (targetIdStr) {
                const targetId = isNaN(Number(targetIdStr)) ? targetIdStr : Number(targetIdStr);
                if (String(targetId) !== String(draggedItemRef.current.id)) {
                    newDropTargetId = targetId;
                }
            }
        }
        
        if (String(newDropTargetId) !== String(dropTargetIdRef.current)) {
            if (dropTargetIdRef.current && listRef.current) {
                const oldTarget = listRef.current.querySelector(`[data-item-id="${dropTargetIdRef.current}"]`);
                oldTarget?.classList.remove(...DROP_TARGET_CLASSES);
            }
            if (newDropTargetId && listRef.current) {
                const newTarget = listRef.current.querySelector(`[data-item-id="${newDropTargetId}"]`);
                newTarget?.classList.add(...DROP_TARGET_CLASSES);
            }
            dropTargetIdRef.current = newDropTargetId;
        }
    
    }, [stopScrolling]);

    const handleTouchEnd = useCallback(() => {
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleTouchEnd);
        document.removeEventListener('touchcancel', handleTouchEnd);
        
        stopScrolling();
    
        if (draggedItemRef.current && dropTargetIdRef.current) {
          const source = draggedItemRef.current;
          const target = items.find(item => String(item.id) === String(dropTargetIdRef.current));
          if (source && target && source.id !== target.id) {
            setMergeConfirmation({ source: source, target: target });
          }
        }
        
        cleanupDragState();
    }, [items, handleTouchMove, stopScrolling, cleanupDragState]);

    const handleTouchStart = useCallback((e: React.TouchEvent<HTMLLIElement>, item: DraggableItem) => {
        const touch = e.touches[0];
        
        draggedItemRef.current = item;
        draggedElementRef.current = e.currentTarget;
        e.currentTarget.classList.add(DRAGGED_ITEM_CLASS);
    
        const ghost = ghostRef.current;
        if (ghost) {
            ghost.style.display = 'block';
            ghost.style.left = `${touch.clientX}px`;
            ghost.style.top = `${touch.clientY}px`;
            ghost.innerText = item.name;
            ghost.style.transform = 'translate(-50%, -50%)';
        }
    
        document.addEventListener('touchmove', handleTouchMove, { passive: false });
        document.addEventListener('touchend', handleTouchEnd);
        document.addEventListener('touchcancel', handleTouchEnd);
    }, [handleTouchMove, handleTouchEnd]);
    
    useEffect(() => {
        return () => {
            stopScrolling();
            cleanupDragState();
        };
    }, [stopScrolling, cleanupDragState]);


    const handleConfirmMerge = async () => {
        if (mergeConfirmation) {
            await onMergeItems(mergeConfirmation.source.id, mergeConfirmation.target.id);
            setMergeConfirmation(null);
        }
    };

    // --- AI Suggestion Handlers ---

    const handleSuggestColors = async () => {
        setSuggestionError(null);
        setIsSuggesting(true);
        try {
            const itemNames = items.map(item => item.name);
            if (itemNames.length > 0) {
                const result = await apiService.suggestColorsForItems(itemNames);
                setSuggestions(result);
            }
        } catch (error: any) {
            setSuggestionError(error.message || 'Došlo k neznámé chybě.');
        } finally {
            setIsSuggesting(false);
        }
    };

    const handleAcceptSuggestions = async () => {
        if (!suggestions || !activeViewId || activeViewId === 'businesses') return;

        const updatedItems = items.map(item => {
            const suggestion = suggestions.find(s => s.name === item.name);
            if (suggestion && item.color) {
                return { ...item, color: suggestion.color };
            }
            return item;
        });

        await saveAllItems(activeViewId, updatedItems as (SettingItem | Currency)[]);
        setSuggestions(null);
    };

    const handleSuggestMerge = async () => {
        setMergeSuggestionError(null);
        setIsSuggestingMerge(true);
        try {
            const itemNames = items.map(item => item.name);
            if (itemNames.length > 1) {
                const geminiResult: MergeSuggestionGroup[] = await apiService.suggestSimilarItemsToMerge(itemNames);
                
                if (geminiResult.length === 0) {
                    alert("AI nenašla žádné položky vhodné ke sloučení.");
                    setMergeSuggestions(null);
                    return;
                }
                
                const itemsByName = new Map(items.map(item => [item.name, item]));

                const mappedSuggestions = geminiResult.map(group => {
                    const target = itemsByName.get(group.target);
                    const sources = group.sources
                        .map(name => itemsByName.get(name))
                        .filter((i: DraggableItem | undefined): i is DraggableItem => !!i && i.name !== group.target);
                    return { target, sources };
                }).filter((g): g is { target: DraggableItem, sources: DraggableItem[] } => !!g.target && g.sources.length > 0);

                if(mappedSuggestions.length > 0) {
                    setMergeSuggestions(mappedSuggestions);
                } else {
                     alert("AI nenašla žádné položky vhodné ke sloučení.");
                }
            }
        } catch (error: any) {
            setMergeSuggestionError(error.message || 'Došlo k neznámé chybě.');
        } finally {
            setIsSuggestingMerge(false);
        }
    };

     const handleConfirmMergeSuggestions = async () => {
        if (!mergeSuggestions) return;
        
        const suggestionsToProcess = mergeSuggestions;
        setMergeSuggestions(null); // Close modal immediately
        
        try {
            const mergePromises = suggestionsToProcess.flatMap(group =>
                group.sources
                    .filter(source => source.id !== group.target.id)
                    .map(source => onMergeItems(source.id, group.target.id))
            );
            if (mergePromises.length > 0) {
                await Promise.all(mergePromises);
            }
        } catch (e) {
            console.error("Failed to merge items", e);
            setMergeSuggestionError("Sloučení se nezdařilo.");
        }
    };

    const formColumnClass = isAddable ? "md:col-span-1" : "";
    const listColumnClass = isAddable ? "md:col-span-2" : "md:col-span-3";

    return (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div 
                ref={ghostRef}
                className="fixed hidden bg-blue-500 text-white px-4 py-2 rounded-lg shadow-xl z-[9999] pointer-events-none text-center"
                style={{ transform: 'translate(-50%, -50%)' }}
            ></div>
            {isAddable && (
                <div className={`${formColumnClass} bg-gray-50 p-6 rounded-lg self-start`}>
                    <h3 className="text-xl font-semibold text-gray-700 mb-4">
                        {editingItem ? 'Upravit položku' : 'Přidat novou položku'}
                    </h3>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label htmlFor="setting-name" className="block text-sm font-medium text-gray-700">Název</label>
                            <input
                                id="setting-name"
                                type="text"
                                value={name}
                                onChange={(e) => {
                                    setName(e.target.value);
                                    setFormError(null);
                                }}
                                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                required
                            />
                        </div>
                        {hasColor && (
                             <>
                                <div>
                                    <label htmlFor="setting-color" className="block text-sm font-medium text-gray-700">Barva</label>
                                    <input
                                        id="setting-color"
                                        type="color"
                                        value={color}
                                        onChange={(e) => setColor(e.target.value)}
                                        className="mt-1 block w-full h-10 p-1 border border-gray-300 rounded-md cursor-pointer"
                                    />
                                </div>
                                
                                {editingItem && (() => {
                                    if (!activeViewId || activeViewId === 'businesses' || typeof editingItem.id !== 'number') return null;
                                    const itemHistory = (perItemColorHistory as Record<ManageableType, Record<number, string[]>>)?.[activeViewId as ManageableType]?.[editingItem.id] || [];
                                    if (itemHistory.length === 0) return null;

                                    return (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mt-4 mb-2">Historie pro tuto položku</label>
                                            <div className="grid grid-cols-8 gap-2">
                                                {itemHistory.map(historyColor => (
                                                    <button
                                                        type="button"
                                                        key={historyColor}
                                                        className="w-8 h-8 rounded-full border border-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                                        style={{ backgroundColor: historyColor }}
                                                        onClick={() => setColor(historyColor)}
                                                        aria-label={`Vybrat barvu ${historyColor}`}
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })()}

                                {colorHistory.length > 0 && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mt-4 mb-2">Naposledy použité (globálně)</label>
                                        <div className="grid grid-cols-8 gap-2">
                                            {colorHistory.map(historyColor => (
                                                <button
                                                    type="button"
                                                    key={historyColor}
                                                    className="w-8 h-8 rounded-full border border-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                                    style={{ backgroundColor: historyColor }}
                                                    onClick={() => setColor(historyColor)}
                                                    aria-label={`Vybrat barvu ${historyColor}`}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                )}
                             </>
                        )}
                        {formError && <p className="text-sm text-red-600 pt-1">{formError}</p>}
                        <div className="flex items-center gap-4 pt-2">
                            <button
                                type="submit"
                                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            >
                                {editingItem ? 'Uložit změny' : 'Přidat'}
                            </button>
                            {editingItem && (
                                <button
                                    type="button"
                                    onClick={handleCancelEdit}
                                    className="text-sm text-gray-600 hover:underline"
                                >
                                    Zrušit
                                </button>
                            )}
                        </div>
                    </form>
                </div>
            )}
            <div className={`${listColumnClass} relative`}>
                {isRefreshing && (
                    <div className="absolute inset-0 bg-white bg-opacity-75 z-10 flex items-center justify-center rounded-lg">
                        <Spinner text="Aktualizuji..." />
                    </div>
                )}
                 <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
                    <h3 className="text-xl font-semibold text-gray-700">{title}</h3>
                    <div className="flex items-center gap-2">
                        {hasColor && items.length > 0 && (
                            <button
                                onClick={handleSuggestColors}
                                disabled={isSuggesting || isSuggestingMerge}
                                className="flex items-center gap-2 px-3 py-1.5 text-sm bg-indigo-100 text-indigo-700 rounded-md hover:bg-indigo-200 disabled:opacity-50 transition-colors"
                                title="Nechat AI navrhnout barvy pro všechny položky v tomto seznamu."
                            >
                                {isSuggesting ? (
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-700"></div>
                                ) : (
                                    <MagicIcon className="w-4 h-4" />
                                )}
                                Navrhnout barvy (AI)
                            </button>
                        )}
                         {items.length > 1 && (
                            <button
                                onClick={handleSuggestMerge}
                                disabled={isSuggestingMerge || isSuggesting}
                                className="flex items-center gap-2 px-3 py-1.5 text-sm bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 disabled:opacity-50 transition-colors"
                                title="Nechat AI navrhnout sloučení podobných položek."
                            >
                                {isSuggestingMerge ? (
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-700"></div>
                                ) : (
                                    <GitMergeIcon className="w-4 h-4" />
                                )}
                                Navrhnout sloučení (AI)
                            </button>
                        )}
                    </div>
                </div>
                 {(suggestionError || mergeSuggestionError) && (
                    <div className="mb-4 p-3 bg-red-100 text-red-700 text-sm rounded-md border border-red-200">
                        <strong>Chyba:</strong> {suggestionError || mergeSuggestionError}
                    </div>
                )}
                <div className="flex items-center gap-4 mb-4">
                    {showFilter && (
                        <input
                            type="text"
                            placeholder={`Filtrovat z ${items.length} položek...`}
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                            className="flex-grow w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        />
                    )}
                    <div className="flex items-center gap-2 flex-shrink-0">
                        <label htmlFor="page-size-select" className="text-sm text-gray-600 whitespace-nowrap">Na stránku:</label>
                        <select
                            id="page-size-select"
                            value={pageSize}
                            onChange={handlePageSizeChangeInternal}
                            className="block w-auto pl-3 pr-8 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                        >
                            <option value="10">10</option>
                            <option value="20">20</option>
                            <option value="40">40</option>
                            <option value="60">60</option>
                            <option value="0">Vše</option>
                        </select>
                    </div>
                </div>
                <div className="bg-white rounded-lg shadow overflow-hidden">
                    <ul ref={listRef} className="divide-y divide-gray-200">
                        {paginatedItems.map(item => (
                            <li 
                                key={item.id}
                                data-item-id={item.id}
                                onClick={() => handleEditClick(item)}
                                draggable
                                onDragStart={(e) => handleDragStart(e, item)}
                                onDragOver={handleDragOver}
                                onDragEnter={(e) => handleDragEnter(e, item)}
                                onDrop={(e) => handleDrop(e, item)}
                                onDragEnd={handleDragEnd}
                                onTouchStart={(e) => handleTouchStart(e, item)}
                                className="px-6 py-4 flex items-center justify-between transition-all cursor-grab select-none no-touch-callout"
                            >
                                <div className="flex items-center gap-2">
                                    <span 
                                        className={`px-3 py-1 text-sm font-medium rounded-full ${!hasColor && 'bg-gray-200 text-gray-800'}`}
                                        style={hasColor && item.color ? { backgroundColor: item.color, color: getContrastColor(item.color) } : {}}
                                    >
                                        {item.name}
                                    </span>
                                     {typeof item.count === 'number' && (
                                        <span className="text-xs bg-gray-200 text-gray-600 font-medium px-1.5 py-0.5 rounded-full" title={`Použito v ${item.count} účtenkách`}>
                                            {item.count}
                                        </span>
                                    )}
                                </div>
                                <div className="flex items-center gap-4">
                                    <button onClick={(e) => { e.stopPropagation(); handleEditClick(item); }} className="text-gray-500 hover:text-blue-600" title="Upravit">
                                        <EditIcon className="w-5 h-5" />
                                    </button>
                                    {isDeletable && (
                                        <button onClick={(e) => { e.stopPropagation(); setItemToDelete(item); }} className="text-gray-500 hover:text-red-600" title="Smazat">
                                            <DeleteIcon className="w-5 h-5" />
                                        </button>
                                    )}
                                </div>
                            </li>
                        ))}
                        {filteredItems.length === 0 && (
                            <li className="px-6 py-4 text-center text-gray-500">
                                Žádné položky k zobrazení.
                            </li>
                        )}
                    </ul>
                </div>
                <div className="mt-4">
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                    />
                </div>
            </div>
            {isDeletable && (
                <ConfirmModal
                    isOpen={!!itemToDelete}
                    onClose={() => setItemToDelete(null)}
                    onConfirm={handleConfirmDelete}
                    title="Opravdu smazat?"
                    confirmText="Smazat"
                    confirmClassName="bg-red-600 text-white rounded-md hover:bg-red-700"
                >
                    {itemToDelete && <p>Chystáte se trvale odstranit položku "{itemToDelete.name}".</p>}
                </ConfirmModal>
            )}

            {mergeConfirmation && (
                 <ConfirmModal
                    isOpen={!!mergeConfirmation}
                    onClose={() => setMergeConfirmation(null)}
                    onConfirm={handleConfirmMerge}
                    title="Sloučit položky?"
                    confirmText="Sloučit"
                    confirmClassName="bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                    <div className="text-sm text-gray-700 space-y-2">
                        <p>
                           Opravdu chcete sloučit položku <strong>"{mergeConfirmation.source.name}"</strong> do položky <strong>"{mergeConfirmation.target.name}"</strong>?
                        </p>
                        <p className="font-semibold text-orange-700">
                           Původní položka "{mergeConfirmation.source.name}" bude smazána. Tato akce je nevratná.
                        </p>
                    </div>
                </ConfirmModal>
            )}
             {suggestions && (
                <ColorSuggestionModal
                    isOpen={!!suggestions}
                    onClose={() => setSuggestions(null)}
                    onConfirm={handleAcceptSuggestions}
                    suggestions={suggestions}
                    currentItems={items}
                />
            )}
            {mergeSuggestions && (
                <MergeSuggestionModal
                    isOpen={!!mergeSuggestions}
                    onClose={() => setMergeSuggestions(null)}
                    onConfirm={handleConfirmMergeSuggestions}
                    suggestions={mergeSuggestions}
                />
            )}
        </div>
    );
};